import { Injectable, inject } from "@angular/core";
import {
  Observable,
  catchError,
  defer,
  distinctUntilChanged,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  timeout,
} from "rxjs";

import { IdCardImageRolloutExperimentKeyService } from "../experiments/id-card-image-rollout/id-card-image-rollout.service";
import { AuthService } from "../shared/services/auth.service";

const ROUTE_DECISION_TIMEOUT_MS = 1000;

/** Keeps one module decision for each user during this application instance. */
@Injectable({ providedIn: "root" })
export class OptimizedIdCardRouteDecision {
  private readonly rollout = inject(IdCardImageRolloutExperimentKeyService);
  private readonly authService = inject(AuthService);
  private readonly decisionsByUserId = new Map<string, Observable<boolean>>();

  resolve$(): Observable<boolean> {
    return defer(() => {
      const deadline = Date.now() + ROUTE_DECISION_TIMEOUT_MS;

      return this.authService.getFireAuthUser().pipe(
        map((user) => {
          const userId = user?.uid;
          return typeof userId === "string" && userId.length > 0
            ? userId
            : null;
        }),
        distinctUntilChanged(),
        timeout({
          first: ROUTE_DECISION_TIMEOUT_MS,
          with: () => of(null),
        }),
        switchMap((userId) => {
          if (!userId) {
            return of(false);
          }

          let decision$ = this.decisionsByUserId.get(userId);
          if (!decision$) {
            const remainingTimeoutMs = Math.max(0, deadline - Date.now());
            decision$ =
              remainingTimeoutMs === 0
                ? of(false)
                : this.rollout
                    .isOnWhenReadyForUser$(userId, remainingTimeoutMs)
                    .pipe(
                      timeout({
                        first: remainingTimeoutMs,
                        with: () => of(false),
                      }),
                      take(1),
                      catchError(() => of(false))
                    );
            decision$ = decision$.pipe(
              shareReplay({ bufferSize: 1, refCount: false })
            );
            this.decisionsByUserId.set(userId, decision$);
          }

          return decision$;
        }),
        take(1),
        catchError(() => of(false))
      );
    });
  }
}
